// P10 — admin audit dashboard, same pattern as proovereport's own.
// Who did what, and when — reads /api/admin/audit-log + /summary.
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Box, Typography, Container, TextField, Select, MenuItem, Button,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress,
  IconButton, Tooltip,
} from "@mui/material";
import { RestartAlt, Refresh, ChevronLeft, ChevronRight } from "@mui/icons-material";
import axios from "axios";
import AdminPageShell from "./AdminPageShell";
import { formatEST } from "../dateUtils";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
const NAVY = "#00357a";

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("admin_token")}` };
}

// 2026-10-04 fix — real gap, confirmed by grepping every record_audit_event
// call site in main.py directly: rcm_resend_payment_link and
// reset_staff_password are both real, currently-logged actions that had
// no entry here, so they rendered as raw snake_case text in the Action
// column AND were missing entirely from the filter dropdown below.
const ACTION_LABELS = {
  generate_report: "Report Generated",
  upload_report_to_gcs: "Report Delivered",
  // Final admin-audit-logging sweep (2026-10-17) — a pre-existing orphan
  // from before this session's own per-piece sweeps: a manual, human-run,
  // one-off operator backfill with no route/scheduler, so it was never
  // caught by any build piece's own audit-log-checker pass.
  extraction_type_backfill_run: "Report Type Backfill Run (Manual Operator Action)",
  // Part 10 — native-report structured My Summaries. Fired when the
  // frontend's round-tripped structured_results form field can't be used
  // (malformed JSON, or valid JSON that isn't an object) — display-only,
  // never blocks the upload itself (see upload_report_to_gcs's own comment).
  upload_report_to_gcs_structured_results_parse_failed: "Structured Results Parse Failed",
  // Phase 0.5 review-swarm fixes (2026-10-19) — the 4 new structured JSON
  // fields (abnormal_value_insights/clinical_flags/citations/followup_test_
  // suggestions) now have their own sanitizer-failure audit trail, matching
  // structured_results_parse_failed's own precedent immediately above.
  upload_report_to_gcs_phase05_parse_failed: "Phase 0.5 Structured Field Parse Failed",
  // Fired by ocr_engine.extract_summary's own fail-open path (generation
  // time, not upload time) — previously logger.warning only, no audit trail.
  extract_summary_structured_fields_failed: "AI Summary Structured Fields Sanitize Failed",
  // Part 13 sweep (2026-10-17) — concurrent-upload race protection + stale
  // self-service blob cleanup, found missing by the post-build review
  // swarm's audit-log-checker (admin-visibility gap: these fired real
  // events with no label, rendering as raw snake_case).
  report_summary_dedup_race_detected: "Concurrent Duplicate Report Submission Detected",
  report_summary_retraction_cleared_on_reupload: "Retraction Cleared — Report Re-Uploaded",
  report_summary_superseded_blob_deleted: "Superseded Report File Cleaned Up",
  report_summary_superseded_blob_delete_failed: "Superseded Report File Cleanup Failed",
  report_summary_superseded_blob_delete_skipped: "Superseded Report File Cleanup Skipped",
  report_summaries_unique_index_creation_failed: "Report De-duplication Index Could Not Be Created (Admin Action Needed)",
  // 2026-10-21 fix (chunk-10 trace finding) — this session's own audit_log
  // TTL-index-at-startup fix added the write side but missed this label,
  // same sibling pattern as the two startup-index-failure events above.
  audit_log_ttl_index_creation_failed: "Audit Log Retention Index Could Not Be Created (Admin Action Needed)",
  requisition_extract: "Requisition Extracted",
  requisition_download: "Requisition Downloaded",
  create_user: "Staff Account Created",
  rcm_resend_payment_link: "Payment Link Resent",
  // 2026-10-05 addition (plan A.6) — the resend action now logs a failure
  // event on every error branch, not just the success tail.
  rcm_resend_payment_link_failed: "Payment Link Resend Failed",
  // Proove-parity build, Part 5 — RCM dashboard's new per-panel resend
  // action (distinct from the whole-account resend above).
  rcm_resend_panel_payment_link: "Panel Payment Link Resent",
  rcm_resend_panel_payment_link_failed: "Panel Payment Link Resend Failed",
  reset_staff_password: "Staff Password Reset",
  // 2026-10-05 additions (plan Section C) — extending audit-log coverage
  // to every previously-untracked state-changing route.
  delete_gcs_report: "Report Deleted",
  delete_gcs_reports_batch: "Reports Batch-Deleted",
  delete_gcs_folder: "Report Folder Deleted",
  requisition_save: "Requisition Edited",
  create_lab: "Lab Created",
  update_lab: "Lab Updated",
  delete_lab: "Lab Deleted",
  add_report_type: "Report Type Added",
  update_report_type: "Report Type Updated",
  delete_report_type: "Report Type Deleted",
  staff_login: "Staff Login",
  staff_login_failed: "Staff Login Failed",
  admin_login: "Admin Login",
  admin_login_failed: "Admin Login Failed",
  // 2026-10-08 additions — every other record_audit_event call site in
  // THIS app's own main.py (staff-side signature: username, action, lab,
  // details), confirmed by grepping every call site directly. These write
  // to this app's own audit_log collection (same as everything else
  // above), so they belong here even where the action is triggered by a
  // portal-side request (order_requisition_rendered/_upload_failed are
  // logged from the internal render endpoint labariumaiportal calls into).
  identity_match_completed: "Identity Match Completed",
  identity_match_mismatch: "Identity Match — Separate Identity Created",
  address_validation_failed: "Address Validation Failed",
  download_report: "Report Downloaded",
  generate_report_gcs_staging_failed: "Report GCS Staging Failed",
  report_summary_doc_write_failed: "Report Summary Write Failed",
  report_summary_doc_write_skipped: "Report Summary Write Skipped (Portal DB Not Configured)",
  // Phase 0 ("My Results" foundation) additions.
  report_summary_doc_invalid_report_kind: "Report Summary Write Rejected — Invalid Report Kind",
  report_summary_doc_orphaned: "Report Summary Row Has No Linked Identity",
  native_report_identity_pinned: "Native Report — Clinician/Patient Identity Mismatch Pinned",
  // 2026-10-19 additions (CRITICAL PHI cross-disclosure fix, security-
  // auditor swarm) — the sibling-flavor pin above now verifies a
  // patient_name_hash/patient_dob_hash match before pinning; these three
  // cover the new blocked/failed outcomes so an admin can actually see
  // them surfaced, not just logged.
  native_report_identity_mismatch_blocked: "Native Report — Identity Mismatch Blocked (Upload Rejected)",
  native_report_identity_pin_lookup_failed: "Native Report — Sibling Identity Lookup Failed",
  native_report_identity_pin_record_fetch_failed: "Native Report — Pinned Identity Record Fetch Failed",
  upload_report_to_gcs_lab_internal_copy_failed: "Lab-Internal Archive Copy Failed",
  order_requisition_rendered: "Order Requisition Rendered",
  order_requisition_upload_failed: "Order Requisition Upload Failed",
  // 2026-10-13 addition — Review & Sign pre-submission PDF preview (the
  // render-only, side-effect-free counterpart to order_requisition_
  // rendered above — see /internal/preview-order-requisition). Kept as
  // "previewed" (past-tense verb), matching this map's own existing
  // rendered/upload_failed naming style, not PORTAL_ACTION_LABELS' own
  // "issued" convention below — these are two separate apps' own audit
  // vocabularies, same rendered-vs-issued split that already exists
  // between this action and clinician_order_requisition_url_issued.
  order_requisition_previewed: "Order Requisition Previewed",
  order_requisition_preview_failed: "Order Requisition Preview Failed",
  order_requisition_preview_access_denied: "Order Requisition Preview — Access Denied",
  password_changed: "Password Changed",
  // 2026-10-14 fix (audit sweep) — identity_resolution_skipped_no_dob
  // removed from THIS map: confirmed dead here specifically — this app's
  // own main.py now always calls resolve_identity() using an
  // "unknown_dob" sentinel instead of skipping it (main.py:2440), so this
  // app never emits this action any more. The identically-named entry
  // further below in PORTAL_ACTION_LABELS is NOT dead — labariumaiportal's
  // own main.py (a separate app/action-set) still emits this action for
  // real (main.py:3487) and that label stays.
  // Lab Orders dashboard — admin (cross-lab) and staff (own-lab-only)
  // viewing of the order-browsing tree/detail, audited per the founder's
  // explicit requirement for this feature.
  admin_order_records_viewed: "Admin Viewed Lab Orders",
  admin_order_records_failed: "Admin Lab Orders Failed",
  admin_order_record_detail_viewed: "Admin Viewed Order Detail",
  admin_order_record_detail_failed: "Admin Order Detail Failed",
  admin_patient_test_results_viewed: "Admin Viewed Patient Test Results",
  admin_patient_test_results_failed: "Admin Patient Test Results Failed",
  staff_lab_orders_viewed: "Staff Viewed Lab Orders",
  staff_lab_orders_failed: "Staff Lab Orders Failed",
  staff_lab_order_detail_viewed: "Staff Viewed Order Detail",
  staff_lab_order_detail_failed: "Staff Order Detail Failed",
  admin_awaiting_proove_report_viewed: "Admin Awaiting Proove Report Viewed",
  admin_awaiting_proove_report_failed: "Admin Awaiting Proove Report Failed",
  // 2026-10-14 fix (confirmed real gap, found by a thorough audit sweep)
  // — these 8 are logged by THIS app's own record_audit_event (into this
  // app's own local audit_log collection, viewed under the default
  // "staff" source), not labariumaiportal's — they were mistakenly added
  // to PORTAL_ACTION_LABELS below instead of here, so an admin on the
  // default tab saw them as raw snake_case and they were missing from
  // that tab's own action-filter dropdown.
  admin_all_identities_viewed: "Admin Viewed All Identities",
  admin_all_identities_failed: "Admin All Identities Failed",
  rcm_reset_portal_credentials: "Admin Reset Portal User Credentials",
  rcm_reset_portal_credentials_failed: "Admin Reset Portal Credentials Failed",
  rcm_change_portal_email: "Admin Changed Portal User Email",
  rcm_change_portal_email_failed: "Admin Change Portal Email Failed",
  rcm_create_portal_clinician: "Admin Created Portal Clinician",
  rcm_create_portal_clinician_failed: "Admin Create Portal Clinician Failed",
  // 2026-10-23 addition (clinician invite-only build, Piece 3) — replaces
  // the create-upfront rcm_create_portal_clinician pair above with the
  // invite-only flow's own action names. The old pair is left labeled
  // here too (its route was deleted, but any pre-cutover historical rows
  // under the old names should still render readable, not raw snake_case).
  rcm_invite_portal_clinician: "Admin Invited Portal Clinician",
  rcm_invite_portal_clinician_failed: "Admin Invite Portal Clinician Failed",
  // 2026-10-26 addition — sales-rep creation proxy (new "+ Create Sales
  // Rep" button on this app's own User Dashboard, same pattern as the
  // clinician-invite proxy directly above).
  rcm_create_portal_sales_rep: "Admin Created Portal Sales Rep",
  rcm_create_portal_sales_rep_failed: "Admin Create Portal Sales Rep Failed",
  // 2026-10-16 (NPPES NPI verification piece) — preview-only proxy for
  // UserDashboard.js's admin clinician-creation dialog.
  rcm_verify_portal_npi: "Admin Previewed NPI Registry Check",
  rcm_verify_portal_npi_failed: "Admin NPI Registry Check Preview Failed",
  staff_awaiting_proove_report_viewed: "Staff Awaiting Proove Report Viewed",
  staff_awaiting_proove_report_failed: "Staff Awaiting Proove Report Failed",
  // 2026-10-12 fix — audit-coverage sweep: every other record_audit_event
  // action logged anywhere in this app's own main.py, confirmed by
  // parsing every call site directly (not just grepping — multi-line
  // calls and bracketed actor expressions were accounted for). None of
  // these had a label before, so they rendered as raw snake_case text.
  add_report_type_failed: "Add Report Type Failed",
  admin_audit_log_failed: "Admin Audit Log Failed",
  admin_audit_log_summary_failed: "Admin Audit Log Summary Failed",
  admin_audit_log_summary_viewed: "Admin Audit Log Summary Viewed",
  admin_audit_log_viewed: "Admin Audit Log Viewed",
  admin_auth_rejected: "Admin Auth Rejected",
  admin_identity_linking_patients_failed: "Admin Identity Linking Patients Failed",
  admin_identity_linking_patients_viewed: "Admin Identity Linking Patients Viewed",
  admin_identity_linking_providers_failed: "Admin Identity Linking Providers Failed",
  admin_identity_linking_providers_viewed: "Admin Identity Linking Providers Viewed",
  // 2026-10-22 (Piece 2, one-identity-one-email invariant) — new search/
  // claim-edit routes on the Identity Linking dashboard.
  admin_identity_search_viewed: "Admin Identity Search Viewed",
  admin_identity_search_failed: "Admin Identity Search Failed",
  rcm_edit_identity_claim: "Admin Edited Identity Email Claim",
  rcm_edit_identity_claim_failed: "Admin Edit Identity Claim Failed",
  // 2026-10-23 addition (clinician invite-only build, Piece 5) — the
  // Clinician Identities admin dashboard table and its Release Claim
  // action, same reasoning as the patient-side admin_identity_* rows above.
  admin_clinician_identities_viewed: "Admin Viewed Clinician Identities",
  admin_clinician_identities_failed: "Admin Clinician Identities Failed",
  rcm_release_clinician_claim: "Admin Released Clinician Claim",
  rcm_release_clinician_claim_failed: "Admin Release Clinician Claim Failed",
  admin_list_labs_failed: "Admin List Labs Failed",
  admin_list_labs_viewed: "Admin List Labs Viewed",
  admin_login_rate_limited: "Admin Login Rate Limited",
  admin_order_record_pdf_url: "Admin Order Record PDF URL",
  admin_order_record_pdf_url_failed: "Admin Order Record PDF URL Failed",
  admin_order_supporting_doc_url_failed: "Admin Order Supporting Doc URL Failed",
  admin_order_supporting_doc_url_issued: "Admin Order Supporting Doc URL Issued",
  admin_portal_audit_log_failed: "Admin Portal Audit Log Failed",
  admin_portal_audit_log_viewed: "Admin Portal Audit Log Viewed",
  admin_rcm_clinicians_failed: "Admin RCM Clinicians Failed",
  admin_rcm_clinicians_viewed: "Admin RCM Clinicians Viewed",
  admin_rcm_patients_failed: "Admin RCM Patients Failed",
  admin_rcm_patients_viewed: "Admin RCM Patients Viewed",
  admin_users_failed: "Admin Users Failed",
  admin_users_viewed: "Admin Users Viewed",
  auth_me_viewed: "Auth Me Viewed",
  auth_rejected: "Auth Rejected",
  browse_gcs_failed: "Browse GCS Failed",
  browse_gcs_viewed: "Browse GCS Viewed",
  client_facing_browse_failed: "Client-Facing Dashboard Browse Failed",
  client_facing_browse_viewed: "Client-Facing Dashboard Browse Viewed",
  client_facing_retracted_lookup_failed: "Client-Facing Dashboard Retracted-State Lookup Failed",
  client_facing_preview_url_issued: "Client-Facing Dashboard Preview URL Generated",
  client_facing_preview_url_failed: "Client-Facing Dashboard Preview URL Failed",
  client_facing_extracted_info_viewed: "Client-Facing Dashboard — Extracted Info Viewed",
  client_facing_extracted_info_failed: "Client-Facing Dashboard — Extracted Info Failed",
  client_facing_extracted_info_not_found: "Client-Facing Dashboard — Extracted Info Not Found",
  client_facing_report_retracted: "Client-Facing Dashboard — Report Retracted",
  client_facing_retract_failed: "Client-Facing Dashboard — Retract Failed",
  client_facing_report_restored: "Client-Facing Dashboard — Report Restored",
  client_facing_restore_failed: "Client-Facing Dashboard — Restore Failed",
  client_facing_report_permanently_deleted: "Client-Facing Dashboard — Report Permanently Deleted",
  client_facing_permanent_delete_failed: "Client-Facing Dashboard — Permanent Delete Failed",
  client_facing_permanent_delete_partial_failure: "Client-Facing Dashboard — Permanent Delete Partial Failure (Admin Action Needed)",
  client_facing_pdf_replaced: "Client-Facing Dashboard — PDF Replaced",
  client_facing_pdf_replace_failed: "Client-Facing Dashboard — PDF Replace Failed",
  client_facing_extracted_replaced: "Client-Facing Dashboard — Extracted Data Replaced",
  client_facing_extracted_replace_failed: "Client-Facing Dashboard — Extracted Data Replace Failed",
  create_lab_failed: "Create Lab Failed",
  create_user_failed: "Create User Failed",
  delete_gcs_folder_failed: "Delete GCS Folder Failed",
  delete_gcs_report_failed: "Delete GCS Report Failed",
  delete_gcs_reports_batch_failed: "Delete GCS Reports Batch Failed",
  download_existing_requisition: "Download Existing Requisition",
  download_existing_requisition_failed: "Download Existing Requisition Failed",
  download_gcs_report_url: "Download GCS Report URL",
  download_gcs_report_url_failed: "Download GCS Report URL Failed",
  download_report_failed: "Download Report Failed",
  generate_report_failed: "Generate Report Failed",
  get_lab_failed: "Get Lab Failed",
  get_lab_viewed: "Get Lab Viewed",
  get_requisition: "Get Requisition",
  get_requisition_failed: "Get Requisition Failed",
  get_source_pdf: "Get Source PDF",
  get_source_pdf_failed: "Get Source PDF Failed",
  lab_order_detail_failed: "Lab Order Detail Failed",
  list_gcs_reports_failed: "List GCS Reports Failed",
  list_gcs_reports_viewed: "List GCS Reports Viewed",
  list_labs_failed: "List Labs Failed",
  list_labs_viewed: "List Labs Viewed",
  notify_report_ready: "Notify Report Ready",
  notify_report_ready_failed: "Notify Report Ready Failed",
  notify_report_ready_skipped: "Notify Report Ready Skipped",
  order_requisition_schema_access_denied: "Order Requisition Schema Access Denied",
  order_requisition_schema_not_found: "Order Requisition Schema Not Found",
  order_requisition_schema_viewed: "Order Requisition Schema Viewed",
  order_requisition_upload_access_denied: "Order Requisition Upload Access Denied",
  order_supporting_doc_upload_failed: "Order Supporting Doc Upload Failed",
  order_supporting_doc_uploaded: "Order Supporting Doc Uploaded",
  password_change_failed: "Password Change Failed",
  preview_gcs_report_url: "Preview GCS Report URL",
  preview_gcs_report_url_failed: "Preview GCS Report URL Failed",
  preview_report: "Preview Report",
  preview_report_failed: "Preview Report Failed",
  rcm_stats_failed: "RCM Stats Failed",
  rcm_stats_viewed: "RCM Stats Viewed",
  report_cleanup_failed: "Report Cleanup Failed",
  identity_index_unique_index_creation_failed: "Identity Index Unique Index Creation Failed (Duplicate-Identity Protection Down)",
  requisition_download_failed: "Requisition Download Failed",
  requisition_extract_failed: "Requisition Extract Failed",
  requisition_final_pdf_archive_skipped: "Requisition Final PDF Archive Skipped",
  requisition_save_failed: "Requisition Save Failed",
  requisition_schema_viewed: "Requisition Schema Viewed",
  requisition_source_pdf_omitted: "Requisition Source PDF Omitted",
  // Part 12 — required attestation upload (main.py's
  // upload_attestation_pdf and download_requisition's own relocation/merge
  // step for it). Same label style as the sibling requisition_* entries
  // immediately above/below.
  requisition_attestation_upload: "Requisition Attestation Uploaded",
  requisition_attestation_upload_failed: "Requisition Attestation Upload Failed",
  requisition_attestation_pdf_omitted: "Requisition Attestation PDF Omitted",
  // 2026-10-13 addition — new RequestValidationError exception handler in
  // main.py (ported from labariumaiportal's identical one) writes this
  // event for any malformed/malicious request body; without a label here
  // it would render as raw snake_case, same gap the 2026-10-12 sweep
  // comment above fixed for other actions.
  request_validation_failed: "Request Validation Failed",
  reset_staff_password_failed: "Reset Staff Password Failed",
  search_reports_failed: "Search Reports Failed",
  search_reports_viewed: "Search Reports Viewed",
  search_requisitions_viewed: "Search Requisitions Viewed",
  search_requisitions_failed: "Search Requisitions Failed",
  staff_lab_order_pdf_url: "Staff Lab Order PDF URL",
  staff_lab_order_pdf_url_failed: "Staff Lab Order PDF URL Failed",
  staff_lab_order_supporting_doc_url_failed: "Staff Lab Order Supporting Doc URL Failed",
  staff_lab_order_supporting_doc_url_issued: "Staff Lab Order Supporting Doc URL Issued",
  staff_login_rate_limited: "Staff Login Rate Limited",
  staff_or_admin_auth_rejected: "Staff Or Admin Auth Rejected",
  update_report_type_failed: "Update Report Type Failed",
  upload_report_to_gcs_failed: "Upload Report To GCS Failed",
  // 2026-10-22 (Piece 4, one-identity-one-email invariant) — same masked-
  // email 409 the Proove upload routes will also use (Piece 5).
  report_upload_blocked_identity_email_conflict: "Report Upload Blocked — Identity Email Conflict",
  upload_report_to_gcs_self_service_copy_failed: "Upload Report To GCS Self Service Copy Failed",
  // Part 11a — the new extraction_type lookup inside upload_report_to_gcs
  // (config_db.get_lab) is wrapped in its own try/except so an infra blip
  // there degrades to extraction_type=None rather than 500ing an
  // already-delivered report; this is its own audit event.
  upload_report_to_gcs_extraction_type_lookup_failed: "Upload Report Extraction Type Lookup Failed",
  validate_address_failed: "Validate Address Failed",
  validate_address_preview: "Validate Address Preview",
  validate_address_rate_limited: "Validate Address Rate Limited",
  // 2026-10-13 addition — Proove Report Upload feature (main.py:2402-2667,
  // /api/proove-reports/upload + _archive_proove_report) had zero entries
  // here; confirmed by grepping every record_audit_event call site in that
  // range directly.
  proove_report_upload_order_matched: "Proove Report Upload — Order Matched",
  proove_report_upload_manual_fallback_used: "Proove Report Upload — Manual Fallback Used",
  proove_report_uploaded: "Proove Report Uploaded",
  proove_report_upload_failed: "Proove Report Upload Failed",
  proove_report_upload_lab_internal_copy_failed: "Proove Report Upload — Lab-Internal Copy Failed",
  proove_report_identity_mismatch_blocked: "Proove Report Identity Mismatch Blocked",
  // 2026-10-22 (Piece 5, one-identity-one-email invariant) — same shared
  // masked-email 409 conversion as report_upload_blocked_identity_email_
  // conflict (Piece 4).
  proove_upload_blocked_identity_email_conflict: "Proove Upload Blocked — Identity Email Conflict",
  // Proove-parity build, item 16 — combined (patient+clinician one-call)
  // upload endpoint. proove_report_uploaded/proove_report_upload_failed
  // above are reused as-is per flavor; these two are specific to the
  // combined request's own OWN outcome (both failed up front, or exactly
  // one flavor failed after the other already succeeded).
  proove_report_combined_upload_failed: "Proove Report Combined Upload Failed",
  proove_report_combined_upload_partial: "Proove Report Combined Upload — Partial Success",
  order_proove_report_delivered: "Order Proove Report Delivered",
  order_proove_report_delivered_failed: "Order Proove Report Delivered Failed",
  // Issue checklist #11 — written the first time a report is archived for
  // a clinician_patient_records row, same call sites/username/lab as the
  // order_proove_report_delivered pair right above (not PORTAL_ACTION_
  // LABELS below — these fire from labariumaireport's own main.py, under
  // the real staff username, not labariumaiportal's).
  clinician_patient_record_identity_linked: "Patient Record Linked to Identity",
  clinician_patient_record_identity_link_failed: "Patient Record Identity Link Failed",
  clinician_patient_record_identity_link_skipped: "Patient Record Identity Link Skipped (Already Linked)",
  clinician_patient_record_identity_link_conflict: "Patient Record Identity Conflict (Two Identities Resolved)",
  // Proove-parity build, Part 7 — background stats-extraction outcome for
  // a Proove report row (recorded under the staff member who uploaded the
  // report, same `generated_by` identity as the upload itself — never a
  // placeholder). This is a staff-side ACTION_LABELS entry (not
  // PORTAL_ACTION_LABELS below) because record_audit_event for these two
  // actions runs inside labariumaireport's own main.py, under the real
  // staff username, same as the other proove_report_* actions above.
  stats_extraction_succeeded: "Proove Report Stats Extraction Succeeded",
  stats_extraction_failed: "Proove Report Stats Extraction Failed",
  // Part 11a — patient-education topics step, fired from BOTH the
  // Proove-family save point (_run_proove_stats_extraction) and the
  // Labarium-native save point (_run_native_education_topics), both under
  // the real staff username that triggered the underlying upload — same
  // staff-side ACTION_LABELS placement as the stats_extraction_* pair
  // immediately above, for the same reason.
  education_topics_generated: "Patient Education Topics Generated",
  education_topics_failed: "Patient Education Topics Generation Failed",
  // 2026-10-22 addition — PROOVE_REPORT_JSON_ENABLED, same staff-side
  // placement/reasoning as the stats_extraction_*/education_topics_*
  // pairs immediately above (fired from labariumaireport's own main.py
  // under the real staff username).
  proove_report_json_provided: "Proove Report JSON Provided",
  proove_report_json_apply_failed: "Proove Report JSON Apply Failed",
  proove_report_json_lab_internal_copy_failed: "Proove Report JSON Lab-Internal Copy Failed",
  proove_report_json_not_applicable: "Proove Report JSON Not Applicable",
};

// 2026-10-05 addition (plan Section C) — labariumaiportal's own actions
// (patients/clinicians, not staff), read via GET /api/admin/portal-audit-log
// (direct cross-service read into portal's new audit_log collection, same
// pattern as the RCM feature's own portal reads). Separate action
// vocabulary from the staff-side ACTION_LABELS above, so a separate map.
const PORTAL_ACTION_LABELS = {
  patient_registered: "Patient Registered",
  clinician_registered: "Clinician Registered",
  // 2026-10-16 (NPPES NPI verification piece) — cross-checks the typed
  // NPI/name against NPPES on every clinician-creation path, never
  // blocking account creation; see this feature's plan-doc entry.
  // 2026-10-23 wording fix (invite-only build, Piece 4 audit sweep) — this
  // same action now also fires from POST /clinician/complete-profile
  // (identity-claim gate for an invited clinician), not just the original
  // self-registration flow. "Registration" alone was misleading there, so
  // the label was broadened to cover both call sites.
  npi_verification_checked: "Clinician NPI Registry Check",
  npi_verification_previewed: "NPI Registry Check Previewed",
  provider_registry_linked: "Clinician Provider Registry Linked",
  provider_registry_link_failed: "Clinician Provider Registry Link Failed",
  provider_registry_link_skipped: "Clinician Provider Registry Link Skipped (No NPI)",
  email_login: "Patient/Clinician Login",
  email_login_failed: "Patient/Clinician Login Failed",
  admin_login: "Portal Admin Login",
  admin_login_failed: "Portal Admin Login Failed",
  payment_completed: "Payment Completed",
  payment_failed: "Payment Failed",
  payment_link_resent: "Payment Link Resent",
  // 2026-10-08 additions (audit click-through) — these six log under the
  // clinician's `sub`, not their email, but now surface under an email-
  // based identity link too (see backend's get_portal_audit_log sub
  // lookup), so they need real labels like every other action here.
  clinician_signature_captured: "Clinician Signature Captured",
  clinician_signature_replaced: "Clinician Signature Replaced",
  clinician_patient_record_created: "Clinician Patient Record Created",
  clinician_patient_record_updated: "Clinician Patient Record Updated",
  clinician_patient_record_blank_address_self_healed: "Clinician Patient Record — Blank Address Self-Healed",
  // 2026-10-22 (Piece 6, one-identity-one-email invariant) — Order a
  // Test now resolves+links an identity at patient-creation/resolve time
  // (labariumaiportal's own resolve_identity + the ported write-once
  // linker), not just at first-report-archival time the way
  // labariumaireport's identical-named ACTION_LABELS entries above do.
  // Same action names, different source (this app's own record_audit_
  // event, no `lab` kwarg) — both maps need a label since the dashboard
  // selects by source, not by action name.
  clinician_patient_record_identity_linked: "Patient Record Linked to Identity",
  clinician_patient_record_identity_link_failed: "Patient Record Identity Link Failed",
  clinician_patient_record_identity_link_skipped: "Patient Record Identity Link Skipped (Already Linked)",
  clinician_patient_record_identity_link_conflict: "Patient Record Identity Conflict (Two Identities Resolved)",
  clinician_order_blocked_identity_email_conflict: "Order a Test Blocked — Identity Email Conflict",
  // 2026-10-13 additions — audit-coverage sweep found two failure
  // branches in labariumaiportal's main.py that recorded no audit event
  // at all before (only a logger call). Labeled here now that they do.
  clinician_patient_record_sync_failed: "Clinician Patient Record Sync Failed (Order)",
  clinician_patient_practice_name_decrypt_failed: "Clinician Practice Name Decrypt Failed",
  order_submitted: "Order Submitted",
  biobank_consent_recorded: "Biobank Consent Recorded",
  address_validation_failed: "Address Validation Failed",
  report_ready_notified: "Report Ready — Patient Notified",
  // Proove-parity build, Part 7 (security-auditor finding) — recorded by
  // labariumaiportal's own /my-summaries route under the patient's own
  // uid, so this belongs here (PORTAL_ACTION_LABELS), not the staff-side
  // ACTION_LABELS map further up this file.
  my_summaries_stats_decrypt_failed: "My Summaries — Stats Decrypt Failed",
  // Part 10 — same per-row decrypt-failure discipline as stats_json above,
  // for the new structured flagged/full-results table field.
  my_summaries_structured_results_decrypt_failed: "My Summaries — Structured Results Decrypt Failed",
  // 2026-10-08 additions — every other record_audit_event call site in
  // labariumaiportal's own main.py, confirmed by grepping every call site
  // directly against this map. Many more event types were added there
  // over this session's build passes than ever got a label here.
  identity_match_completed: "Identity Match Completed",
  identity_match_mismatch: "Identity Match — Separate Identity Created",
  internal_notification_sent: "Internal Team Notified",
  internal_notification_send_failed: "Internal Team Notification Failed",
  patient_credentials_email_sent: "Patient Credentials Email Sent",
  patient_credentials_email_send_failed: "Patient Credentials Email Failed",
  payment_confirmation_email_sent: "Payment Confirmation Email Sent",
  payment_confirmation_email_send_failed: "Payment Confirmation Email Failed",
  payment_confirmation_sms_sent: "Payment Confirmation SMS Sent",
  payment_confirmation_sms_send_failed: "Payment Confirmation SMS Failed",
  payment_link_email_sent: "Payment Link Email Sent",
  payment_link_email_send_failed: "Payment Link Email Failed",
  payment_link_sms_sent: "Payment Link SMS Sent",
  payment_link_sms_send_failed: "Payment Link SMS Failed",
  welcome_email_sent: "Welcome Email Sent",
  welcome_email_send_failed: "Welcome Email Failed",
  patient_registration_failed: "Patient Registration Failed",
  // 2026-10-14 addition — cross-account PHI-access IDOR fix: a registrant's
  // name+DOB+address matched an identity_id an existing patient account
  // already has, so registration was blocked with 409 rather than handing
  // the new account that identity's real report access. Without this
  // label the admin audit dashboard rendered the raw action string AND
  // (more importantly) this action was missing from the filter dropdown
  // entirely, since that dropdown is built from this same map's keys.
  patient_registration_blocked_identity_already_claimed: "Patient Registration Blocked — Identity Already Claimed",
  // 2026-10-22 (Piece 3, one-identity-one-email invariant) — same
  // generic 409 as the action above, but raised by resolve_identity's own
  // email-claim check rather than the identity_id-reuse pre-check.
  patient_registration_blocked_identity_email_conflict: "Patient Registration Blocked — Identity Email Conflict",
  // 2026-10-23 addition — same "no unlabeled action" discipline as the two
  // patient_registration_blocked_* entries above. Public clinician self-
  // registration is now blocked outright (identity-theft gap: anyone who
  // knew a real physician's name+practice+NPI could create an account
  // under their own email and see that physician's data). See main.py's
  // own "2026-10-23 fix" comment in register().
  clinician_self_registration_blocked: "Clinician Registration Blocked — Self-Registration Disabled",
  clinician_registration_failed: "Clinician Registration Failed",
  // 2026-10-24 fix (flow audit, audit-visibility diff) — this action
  // (a non-rep account hitting a sales-rep-only route) had no label at
  // all, unlike every other sales-rep/invite/self-registration action
  // in this same group.
  sales_rep_access_denied: "Sales Rep Access Denied",
  // 2026-10-23 additions (clinician invite-only build, Piece 4 audit
  // sweep) — these two fire from POST /clinician/complete-profile, the
  // mandatory identity-claim gate an invited clinician must clear.
  // Without labels they rendered as raw snake_case in the dashboard and
  // were missing from the action-filter dropdown.
  clinician_identity_claim_blocked: "Clinician Identity Claim Blocked",
  clinician_profile_completed: "Clinician Profile Completed",
  clinician_signature_capture_failed: "Clinician Signature Capture Failed",
  clinician_welcome_email_sent: "Clinician Welcome Email Sent",
  clinician_welcome_email_send_failed: "Clinician Welcome Email Failed",
  report_ready_email_sent: "Report Ready Email Sent",
  report_ready_notify_failed: "Report Ready Notification Failed",
  report_ready_email_send_failed: "Report Ready Email Failed",
  report_ready_sms_sent: "Report Ready SMS Sent",
  report_ready_sms_send_failed: "Report Ready SMS Failed",
  report_ready_sms_skipped_no_phone: "Report Ready SMS Skipped — No Phone on File",
  report_ready_sms_skipped_disabled: "Report Ready SMS Skipped — SMS Disabled",
  password_changed: "Password Changed",
  password_reset_email_sent: "Password Reset Email Sent",
  password_reset_email_send_failed: "Password Reset Email Failed",
  password_reset_completed: "Password Reset Completed",
  forgot_password_failed: "Forgot Password Attempt — No Such Account",
  clinician_settings_updated: "Clinician Settings Updated",
  clinician_signature_replace_failed: "Clinician Signature Replace Failed",
  order_submission_partial_failure: "Order Submission Rolled Back",
  order_submission_idempotency_race: "Duplicate Order Submission Prevented",
  patient_consent_link_issued: "Patient Consent Link Sent",
  patient_consent_no_email_on_file: "Patient Consent Link — No Email on File",
  patient_consent_email_sent: "Patient Consent Email Sent",
  patient_consent_email_send_failed: "Patient Consent Email Failed",
  patient_consent_sms_sent: "Patient Consent SMS Sent",
  patient_consent_sms_send_failed: "Patient Consent SMS Failed",
  patient_consent_resent: "Patient Consent Link Resent",
  admin_order_search_viewed: "Admin Order Search Viewed",
  admin_order_search_failed: "Admin Order Search Failed",
  patient_consent_dob_verification_failed: "Patient Consent — DOB Verification Failed",
  patient_consent_token_locked: "Patient Consent Link Locked (Too Many Attempts)",
  patient_consent_link_opened: "Patient Consent Link Opened",
  patient_consent_submission_partial_failure: "Patient Consent Submission Rolled Back",
  patient_consent_claim_released_on_cancellation: "Patient Consent Claim Released After Cancellation",
  patient_consent_signed: "Patient Consent Signed",
  // 2026-10-08 addition — broader audit-coverage sweep: these two
  // bot-memory-clear actions are real PHI-adjacent deletes that were
  // previously silent. (A planned google_login pair was removed along
  // with the dead, never-wired-up /google-login backend route itself —
  // see main.py's own history — rather than labeling an unreachable
  // event.)
  patient_bot_memory_cleared: "Patient Bot Memory Cleared",
  clinician_bot_memory_cleared: "Clinician Bot Memory Cleared",
  // 2026-10-12 fix — audit-coverage sweep: every other record_audit_event
  // action logged anywhere in labariumaiportal's own main.py, confirmed
  // by parsing every call site directly (same method as ACTION_LABELS'
  // own sweep above). None of these had a label before, so they rendered
  // as raw snake_case text.
  address_validation_checked: "Address Validation Checked",
  admin_access_denied: "Admin Access Denied",
  admin_clinician_account_created: "Admin Clinician Account Created",
  admin_clinician_account_create_failed: "Admin Clinician Account Create Failed",
  admin_provider_registry_viewed: "Admin Provider Registry Viewed",
  admin_provider_registry_failed: "Admin Provider Registry Failed",
  // 2026-10-23 addition (clinician invite-only build, Piece 3) — replaces
  // the create-upfront admin_clinician_account_* pair above with the
  // invite-only flow's own action names (fired from _invite_clinician_core;
  // today only the /admin/clinicians/invite caller is wired to the admin
  // JWT route, so "admin" is the real, confirmed inviter_role — a future
  // sales-rep caller through /internal/admin-invite-clinician would reuse
  // these same two action names, not a separate pair).
  clinician_invite_sent: "Clinician Invite Sent",
  clinician_invite_failed: "Clinician Invite Failed",
  // 2026-10-23 addition (audit-log-checker finding, sales-rep lab-brand
  // routing review) — these two actions (admin_create_sales_rep) had
  // zero labels, so they rendered as raw snake_case AND were missing
  // from the action-filter dropdown entirely.
  admin_sales_rep_created: "Sales Rep Created",
  admin_sales_rep_create_failed: "Sales Rep Create Failed",
  // 2026-10-13 additions — the two new admin account-recovery actions
  // (POST /admin/users/{sub}/reset-credentials and .../change-email),
  // modeled on prooveai-portal-v2's reset-credentials pattern. Placed here
  // (alphabetically before admin_users_*) to match this map's own sort
  // convention — "admin_user_" sorts before "admin_users_" (underscore
  // precedes 's').
  admin_user_credentials_reset: "Admin Reset User Credentials",
  admin_user_credentials_reset_failed: "Admin Reset User Credentials Failed",
  admin_user_email_change_failed: "Admin Change User Email Failed",
  admin_user_email_changed: "Admin Changed User Email",
  // 2026-10-22 (Piece 2, one-identity-one-email invariant) — portal's own
  // write for the Identity Linking dashboard's claim-edit action (the
  // sync-on-email-change case folds into admin_user_email_changed's own
  // details.identity_claim_synced above, not a separate action).
  admin_identity_claim_changed: "Admin Changed Identity Email Claim",
  admin_identity_claim_change_failed: "Admin Change Identity Claim Failed",
  // 2026-10-23 addition (clinician invite-only build, Piece 5) — portal's
  // own write behind the report-side Release Claim proxy above.
  admin_clinician_claim_released: "Admin Released Clinician Claim",
  admin_clinician_claim_release_failed: "Admin Release Clinician Claim Failed",
  admin_users_view_failed: "Admin Users View Failed",
  admin_users_viewed: "Admin Users Viewed",
  api_me_view_failed: "API Me View Failed",
  api_me_viewed: "API Me Viewed",
  bot_analysis_completed: "Bot Analysis Completed",
  bot_analysis_failed: "Bot Analysis Failed",
  bot_ask_answered: "Bot Ask Answered",
  bot_ask_failed: "Bot Ask Failed",
  bot_memory_save_failed: "Bot Memory Save Failed",
  bot_memory_saved: "Bot Memory Saved",
  clinician_access_denied: "Clinician Access Denied",
  clinician_bot_memory_clear_failed: "Clinician Bot Memory Clear Failed",
  clinician_order_requisition_url_failed: "Clinician Order Requisition URL Failed",
  clinician_order_requisition_url_issued: "Clinician Order Requisition URL Issued",
  clinician_order_supporting_doc_url_failed: "Clinician Order Supporting Doc URL Failed",
  clinician_order_supporting_doc_url_issued: "Clinician Order Supporting Doc URL Issued",
  clinician_orders_list_failed: "Clinician Orders List Failed",
  clinician_orders_listed: "Clinician Orders Listed",
  clinician_patient_edit_failed: "Clinician Patient Edit Failed",
  clinician_patient_last_order_data_view_failed: "Clinician Patient Last Order Data View Failed",
  clinician_patient_last_order_data_viewed: "Clinician Patient Last Order Data Viewed",
  clinician_patient_submit_failed: "Clinician Patient Submit Failed",
  clinician_patient_view_failed: "Clinician Patient View Failed",
  clinician_patient_viewed: "Clinician Patient Viewed",
  clinician_patients_list_failed: "Clinician Patients List Failed",
  clinician_patients_listed: "Clinician Patients Listed",
  clinician_settings_update_failed: "Clinician Settings Update Failed",
  clinician_settings_update_noop: "Clinician Settings Update Noop",
  clinician_settings_view_failed: "Clinician Settings View Failed",
  clinician_settings_viewed: "Clinician Settings Viewed",
  clinician_signature_consent_text_view_failed: "Clinician Signature Consent Text View Failed",
  clinician_signature_consent_text_viewed: "Clinician Signature Consent Text Viewed",
  identity_resolution_skipped_no_dob: "Identity Resolution Skipped No DOB",
  internal_api_key_rejected: "Internal API Key Rejected",
  logged_out: "Logged Out",
  logout_failed: "Logout Failed",
  // 2026-10-15 fix (audit-log-checker finding, Part 4 review) — these two
  // single-item-copay-flow labels sat one letter ("Payment" vs "Payments")
  // from the newer my_payment_items_* labels below, a real misread/
  // misselect risk in a flat alphabetical filter dropdown. Renamed to be
  // unambiguous; action names (the actual filter values) are unchanged.
  my_payment_view_failed: "My Payment (Copay) View Failed",
  my_payment_viewed: "My Payment (Copay) Viewed",
  panel_payment_link_resend_failed: "Panel Payment Link Resend Failed",
  panel_payment_link_resent: "Panel Payment Link Resent",
  my_reports_browse_failed: "My Reports Browse Failed",
  my_reports_browsed: "My Reports Browsed",
  my_reports_retracted_lookup_failed: "My Reports — Retracted-State Lookup Failed",
  my_reports_prefix_fail_closed: "My Reports Prefix Fail Closed",
  my_reports_signed_url_failed: "My Reports Signed URL Failed",
  my_reports_signed_url_issued: "My Reports Signed URL Issued",
  my_reports_signed_url_denied_retracted: "My Reports — Signed URL Denied (Report Retracted)",
  my_summaries_view_failed: "My Summaries View Failed",
  my_summaries_viewed: "My Summaries Viewed",
  // "My Results" ecosystem plan, Phase 1 (core page), 2026-10-21 —
  // GET /my-results, labariumaiportal/backend/main.py. Same sibling
  // pattern as the my_summaries_* entries right above (audit-log-checker
  // finding: these 5 were missing, rendering as raw snake_case and
  // excluded from the admin filter dropdown).
  my_results_viewed: "My Results Viewed",
  my_results_view_failed: "My Results View Failed",
  my_results_field_decrypt_failed: "My Results — Field Decrypt Failed",
  my_results_stats_decrypt_failed: "My Results — Stats Decrypt Failed",
  my_results_structured_results_decrypt_failed: "My Results — Structured Results Decrypt Failed",
  my_results_unexpected_clinician_field_present: "My Results — Unexpected Clinician Field Present (Write-Side Bug Signal)",
  // "My Results" ecosystem plan, Phase 1, Stage 2 — GET /my-results/trends.
  my_results_trends_viewed: "My Results Trends Viewed",
  my_results_trends_failed: "My Results Trends Failed",
  my_results_trends_row_decrypt_failed: "My Results Trends — Row Decrypt Failed",
  my_results_trends_row_date_parse_failed: "My Results Trends — Row Date Parse Failed",
  my_results_trends_row_shape_invalid: "My Results Trends — Row Shape Invalid",
  // "My Results" ecosystem plan, Phase 1, Stage 3 — per-value inline chat.
  my_results_ask_value_answered: "My Results — Ask About Value Answered",
  my_results_ask_value_failed: "My Results — Ask About Value Failed",
  // "My Results" ecosystem plan, Phase 1, Stage 3 — whole-report chat.
  my_results_ask_report_answered: "My Results — Ask About Report Answered",
  my_results_ask_report_failed: "My Results — Ask About Report Failed",
  // "My Results" ecosystem plan, Phase 2 — clinician counterpart.
  my_patients_results_viewed: "My Patients' Results — Viewed",
  my_patients_results_view_failed: "My Patients' Results — View Failed",
  my_patients_results_field_decrypt_failed: "My Patients' Results — Field Decrypt Failed",
  my_patients_results_stats_decrypt_failed: "My Patients' Results — Stats Decrypt Failed",
  my_patients_results_structured_results_decrypt_failed: "My Patients' Results — Structured Results Decrypt Failed",
  my_patients_results_unexpected_patient_field_present: "My Patients' Results — Unexpected Patient Field Present",
  my_patients_results_npi_match_unconfirmed: "My Patients' Results — NPI Match Unconfirmed (Different Clinician)",
  // "My Results" ecosystem plan — "Browse My Tests" dual input mode.
  browse_my_tests_loaded: "Browse My Tests — Loaded Into Chat",
  browse_my_tests_failed: "Browse My Tests — Load Failed",
  browse_my_tests_session_write_skipped: "Browse My Tests — Session Write Skipped (Superseded)",
  // 2026-10-13 addition — Review & Sign pre-submission PDF preview (POST
  // /clinician/orders/preview-requisition-pdf). "issued" to match this
  // map's own existing clinician_order_requisition_url_issued naming
  // (both are portal-side, caller-facing "we handed you back a usable
  // thing" events) — the report-side ACTION_LABELS map above uses
  // "previewed" for its own, separate order_requisition_previewed action,
  // matching ITS OWN existing rendered/upload_failed naming instead; the
  // two apps have always had their own separate action vocabularies (see
  // that map's own comment), not a single shared one.
  order_requisition_preview_failed: "Order Requisition Preview Failed",
  order_requisition_preview_issued: "Order Requisition Preview Issued",
  order_requisition_schema_view_failed: "Order Requisition Schema View Failed",
  order_requisition_schema_viewed: "Order Requisition Schema Viewed",
  order_submission_failed: "Order Submission Failed",
  order_supporting_doc_upload_failed: "Order Supporting Doc Upload Failed",
  order_supporting_doc_uploaded: "Order Supporting Doc Uploaded",
  orderable_tests_view_failed: "Orderable Tests View Failed",
  orderable_tests_viewed: "Orderable Tests Viewed",
  password_change_failed: "Password Change Failed",
  password_reset_failed: "Password Reset Failed",
  patient_bot_memory_clear_failed: "Patient Bot Memory Clear Failed",
  patient_consent_details_failed: "Patient Consent Details Failed",
  patient_consent_details_viewed: "Patient Consent Details Viewed",
  patient_consent_lookup_failed: "Patient Consent Lookup Failed",
  patient_consent_lookup_viewed: "Patient Consent Lookup Viewed",
  patient_consent_requisition_schema_fetch_failed: "Patient Consent Requisition Schema Fetch Failed",
  patient_consent_requisition_schema_viewed: "Patient Consent Requisition Schema Viewed",
  patient_consent_resend_failed: "Patient Consent Resend Failed",
  patient_consent_submit_failed: "Patient Consent Submit Failed",
  patient_consent_verify_failed: "Patient Consent Verify Failed",
  payment_link_resend_failed: "Payment Link Resend Failed",
  rate_limit_exceeded: "Rate Limit Exceeded",
  request_validation_failed: "Request Validation Failed",
  payment_webhook_unrecorded: "Payment Webhook Unrecorded (Admin Action Needed)",
  payment_webhook_replay_skipped: "Payment Webhook Replay (Already Completed)",
  // Proove-parity build, Part 2 — order-time payment link triggering.
  order_payment_link_triggered: "Order Payment Link Sent",
  order_payment_link_skipped: "Order Payment Link Skipped",
  order_payment_link_resent_existing_pending: "Order Payment Link Resent (Already Pending)",
  order_legacy_address_hash_recompute_failed: "Order Payment Link — Legacy Address Hash Recompute Failed",
  item_payment_row_processing_failed: "Combined Checkout Item Processing Failed (Admin Action Needed)",
  item_payment_completed: "Item Payment Completed",
  item_payment_link_email_sent: "Item Payment Link Email Sent",
  item_payment_link_email_send_failed: "Item Payment Link Email Failed",
  item_payment_link_sms_sent: "Item Payment Link SMS Sent",
  item_payment_link_sms_send_failed: "Item Payment Link SMS Failed",
  item_payment_confirmation_email_sent: "Item Payment Confirmation Email Sent",
  item_payment_confirmation_email_send_failed: "Item Payment Confirmation Email Failed",
  item_payment_confirmation_sms_sent: "Item Payment Confirmation SMS Sent",
  item_payment_confirmation_sms_send_failed: "Item Payment Confirmation SMS Failed",
  item_payment_confirmation_send_failed: "Item Payment Confirmation Send Failed",
  item_payment_confirmation_skipped: "Item Payment Confirmation Skipped (No Contact Info)",
  item_payment_webhook_replay_skipped: "Item Payment Webhook Replay (Already Completed)",
  item_payment_amount_mismatch_blocked: "Item Payment BLOCKED — Amount Mismatch (Admin Action Needed)",
  item_payment_identity_mismatch_blocked: "Item Payment Confirmation BLOCKED — Identity Mismatch (Admin Action Needed)",
  // Proove-parity build, Part 3 — registration-time payment flow.
  patient_registered_copay_skip_already_paid: "Registration Copay Skipped (Already Paid)",
  patient_registered_copay_check_failed: "Registration Copay Check Failed",
  patient_registered_copay_check_skipped_no_dob: "Registration Copay Check Skipped (No DOB)",
  patient_registered_panel_check_failed: "Registration Panel Check Failed",
  patient_registered_self_pay_now_no_dob: "Registration Self-Pay Panels Skipped (No DOB)",
  patient_registered_unrecognized_bill_to_defaulted: "Registration Unrecognized Bill-To Defaulted",
  checkout_session_created: "Checkout Session Created",
  checkout_session_create_failed: "Checkout Session Creation Failed",
  checkout_session_create_skipped: "Checkout Session Creation Skipped",
  checkout_session_reused_existing_pending: "Checkout Session Reused Existing Pending Request",
  // Item 15 (2026-10-16) — "one-step checkout", labariumaiportal's
  // registration-time self-pay+"now" step: ≥1 panel + the $0.99/mo
  // subscription in a single Stripe Checkout Session instead of two
  // sequential redirects. New multi-item group events, distinct from the
  // Part 2/3/9 single-item events just above (which this build item never
  // touches/never fires differently).
  checkout_group_created: "Combined Checkout Session Created",
  checkout_group_create_failed: "Combined Checkout Session Creation Failed",
  checkout_group_payment_completed: "Combined Checkout Payment Completed",
  checkout_group_webhook_replay_skipped: "Combined Checkout Webhook Replay (Already Completed)",
  checkout_group_amount_mismatch_blocked: "Combined Checkout BLOCKED — Amount Mismatch (Admin Action Needed)",
  checkout_group_session_mismatch: "Combined Checkout BLOCKED — Session Mismatch (Admin Action Needed)",
  // 2026-10-16 fix (security-auditor finding, pre-integration, item 15
  // close-out) — covers both the "ppr:" (single-item) and "pprg:"
  // (combined) checkout schemes; a routine abandoned/expired checkout,
  // not an error.
  item_checkout_expired_or_failed: "Checkout Abandoned or Failed (Routine)",
  // Proove-parity build, Part 4 — MyPayments per-item view.
  my_payment_items_viewed: "My Payments Viewed",
  my_payment_items_view_failed: "My Payments View Failed",
  my_payment_items_missing_identity_hash: "My Payments Missing Identity Hash",
  checkout_token_minted: "Checkout Token Minted",
  checkout_token_mint_failed: "Checkout Token Mint Failed",
  stripe_webhook_processed: "Stripe Webhook Processed",
  stripe_webhook_processing_failed: "Stripe Webhook Processing Failed",
  stripe_webhook_rejected: "Stripe Webhook Rejected",
  // Proove-parity build, Part 6a — typed (structured) Proove-family bot
  // analysis, distinct from the existing generic bot_analysis_completed/
  // _failed above (those cover the freeform, non-Proove analysis path).
  typed_analysis_invalid_report_type: "Typed Analysis Invalid Report Type",
  typed_analysis_failed: "Typed Analysis Failed",
  typed_analysis_succeeded: "Typed Analysis Succeeded",
  // Proove-parity build, Part 6 follow-up — session-scoped (never
  // persisted) follow-up Q&A on a typed Proove analysis, and the race
  // guard that skips writing typed session state if a different upload
  // landed for this account mid-analysis (cross-patient-mixing safety).
  typed_analysis_session_write_skipped: "Typed Analysis Session Write Skipped",
  // 2026-10-19 fix (real gap, confirmed via cross-app contract inventory)
  // — generic (non-typed) bot's own cross-patient-mixing race guard had
  // no label at all, unlike its typed sibling right above. Portal-side
  // action, surfaced here same as every other portal action in this map.
  bot_ask_session_write_skipped: "Bot Session Write Skipped",
  // 2026-10-16 (audit-log-checker-flagged real gap) — these two failure
  // paths used to be unaudited (a bare unhandled exception / a
  // logger-only warning with no admin-visible trail).
  typed_analysis_session_write_failed: "Typed Analysis Session Write Failed",
  typed_session_state_clear_failed: "Typed Session State Clear Failed",
  typed_ask_succeeded: "Typed Follow-Up Answered",
  typed_ask_failed: "Typed Follow-Up Failed",
  trends_view_failed: "Trends View Failed",
  trends_viewed: "Trends Viewed",
  user_id_access_denied: "User ID Access Denied",
  // Proove-parity build, Part 9 — $0.99/mo monthly AI subscription
  // (registration-time opt-in + the 3 Stripe subscription-lifecycle
  // webhook events: invoice.paid/invoice.payment_failed/customer.
  // subscription.deleted). patient_registered's own details gained
  // subscription_optin/subscription_offered fields (no new label needed —
  // same action, richer details).
  subscription_renewal_skipped_initial_invoice: "Subscription Renewal — Initial Invoice Skipped (Already Confirmed)",
  subscription_renewal_replay_skipped: "Subscription Renewal — Replayed Invoice Skipped",
  subscription_invoice_paid_unmatched: "Subscription Renewal — No Matching Subscription (Admin Action Needed)",
  subscription_renewal_confirmed: "Subscription Renewal Confirmed",
  subscription_invoice_payment_failed_unmatched: "Subscription Payment Failed — No Matching Subscription (Admin Action Needed)",
  subscription_payment_failed: "Subscription Payment Failed",
  subscription_payment_failed_webhook_replay_skipped: "Subscription Payment-Failed Webhook Replay Skipped",
  subscription_payment_failed_email_sent: "Subscription Payment-Failed Email Sent",
  subscription_payment_failed_email_send_failed: "Subscription Payment-Failed Email Failed",
  subscription_payment_failed_sms_sent: "Subscription Payment-Failed SMS Sent",
  subscription_payment_failed_sms_send_failed: "Subscription Payment-Failed SMS Failed",
  subscription_cancel_unmatched: "Subscription Cancellation — No Matching Subscription (Admin Action Needed)",
  subscription_cancel_replay_skipped: "Subscription Cancellation — Replay Skipped",
  subscription_canceled: "Subscription Canceled",
  subscription_canceled_email_sent: "Subscription-Canceled Email Sent",
  subscription_canceled_email_send_failed: "Subscription-Canceled Email Failed",
  subscription_canceled_sms_sent: "Subscription-Canceled SMS Sent",
  subscription_canceled_sms_send_failed: "Subscription-Canceled SMS Failed",
  // 2026-10-16 additions — swarm-review fixes (security-auditor/audit-
  // log-checker findings) applied after the initial Part 9 build pass.
  subscription_invoice_ignored_billing_reason: "Subscription Invoice Ignored (Unrecognized Billing Reason)",
  subscription_phone_decrypt_failed: "Subscription Notice — Phone Decrypt Failed",
  subscription_cancel_notice_skipped_no_user: "Subscription Canceled — Patient Notice Skipped (No User On File)",
  subscription_payment_failed_notice_skipped_no_contact: "Subscription Payment-Failed Notice Skipped (No Contact Info)",
  subscription_canceled_notice_skipped_no_contact: "Subscription-Canceled Notice Skipped (No Contact Info)",
  // Item 14 (2026-10-16) — Stripe Billing Portal (plan decision #5): self-
  // serve cancel/payment-method-update/restart-via-reuse/receipt history.
  // billing_portal_session_created is this piece's own real auditable
  // moment for "patient opened the Billing Portal" (receipt-viewing itself
  // fires no Stripe webhook at all, per the plan's own research).
  billing_portal_session_created: "Billing Portal Opened",
  billing_portal_session_create_failed: "Billing Portal Open Failed",
  // customer.subscription.updated — a genuine scheduled-cancel or reversal
  // of one, detected by diffing previous_attributes (never fired on this
  // same event type's own unrelated field churn, e.g. a renewal's
  // current_period_end bump — deliberately no label/no event for that
  // no-op case, since it was never audited in the first place). Does NOT
  // mean the subscription actually ended — only customer.subscription.
  // deleted's own subscription_canceled event above means that.
  subscription_cancel_scheduled: "Subscription Cancellation Scheduled (Still Active Until Period End)",
  subscription_cancel_reversed: "Subscription Cancellation Reversed (Still Active)",
  subscription_cancel_schedule_unmatched: "Subscription Cancellation Schedule Change — No Matching Subscription (Admin Action Needed)",
  subscription_cancel_schedule_replay_skipped: "Subscription Cancellation Schedule — Replay/Stale Event Skipped",
  subscription_cancel_schedule_notice_identity_mismatch_blocked: "Subscription Cancellation Schedule — Notice BLOCKED (Identity Mismatch)",
  subscription_cancel_schedule_notice_skipped_no_user: "Subscription Cancellation Schedule — Notice Skipped (No User On File)",
  // payment_method.attached / customer.updated — whichever one Stripe's
  // own portal "update payment method" flow actually fires (both enabled
  // defensively; real event name always recorded, never collapsed into a
  // guess) — see stripe_event_type in this event's own details.
  payment_method_updated: "Payment Method Updated",
  payment_method_update_unmatched: "Payment Method Update — No Tracked Patient (Routine)",
  payment_method_update_replay_skipped: "Payment Method Update — Replayed Event Skipped",
  payment_method_update_notice_identity_mismatch_blocked: "Payment Method Update — Notice BLOCKED (Identity Mismatch)",
  payment_method_update_notice_skipped_no_user: "Payment Method Update — Notice Skipped (No User On File)",
  my_payment_items_subscription_history_check_failed: "My Payments Subscription-History Check Failed",
  // Item 17 fix — real two-sided diff against every record_audit_event
  // call site added across Part 11 (11a/11b-1/11b-2) in labariumaiportal's
  // own main.py: 29 actions with no entry here at all, so they rendered as
  // raw snake_case AND were missing from this tab's own filter dropdown.
  // The three *_identity_mismatch_blocked entries below mirror the existing
  // item_payment_identity_mismatch_blocked label's own "BLOCKED — notice
  // only, not the underlying payment/cancellation itself" framing.
  monthly_digest_run_skipped_disabled: "Monthly Digest — Run Skipped (Feature Disabled)",
  monthly_digest_feed_pool_refresh_completed: "Monthly Digest — News Feed Pool Refresh Completed",
  monthly_digest_feed_pool_refresh_breakdown: "Monthly Digest — News Feed Pool Refresh Breakdown",
  monthly_digest_trigger_completed: "Monthly Digest — Cron Trigger Completed",
  monthly_digest_identity_failed: "Monthly Digest — Identity Run Failed",
  monthly_digest_eligibility_check_failed: "Monthly Digest — Eligibility Check Failed",
  monthly_digest_proove_no_taxonomy_mapping: "Monthly Digest — Proove Report Has No Taxonomy Mapping",
  monthly_digest_education_topics_decrypt_failed: "Monthly Digest — Education Topics Decrypt Failed",
  monthly_digest_phi_topic_dropped: "Monthly Digest — PHI Topic Dropped",
  monthly_digest_news_hallucinated_link_dropped: "Monthly Digest — Hallucinated News Link Dropped",
  monthly_digest_live_search_fallback_disabled: "Monthly Digest — Live Search Fallback Disabled",
  monthly_digest_news_draft_phi_rejected: "Monthly Digest — News Draft Rejected (PHI Risk)",
  monthly_digest_narrative_already_current: "Monthly Digest — Narrative Already Current (Skipped)",
  monthly_digest_stats_decrypt_failed: "Monthly Digest — Stats Decrypt Failed",
  monthly_digest_narrative_failed: "Monthly Digest — Narrative Generation Failed",
  monthly_digest_news_search_completed: "Monthly Digest — News Search Completed",
  monthly_digest_narrative_generated: "Monthly Digest — Narrative Generated",
  monthly_digest_locked_specimen_missing: "Monthly Digest — Locked Specimen Missing",
  monthly_digest_specimen_locked: "Monthly Digest — Specimen Locked",
  monthly_digest_native_extraction_type_missing: "Monthly Digest — Native Extraction Type Missing",
  monthly_digest_proove_row_ambiguous_report_kind_skipped: "Monthly Digest — Proove Row Skipped (Ambiguous Report Kind)",
  monthly_digest_native_row_non_patient_skipped: "Monthly Digest — Native Row Skipped (Not a Patient Report)",
  monthly_digest_row_failed: "Monthly Digest — Report Row Failed",
  monthly_digest_email_sent: "Monthly Digest Email Sent",
  monthly_digest_email_send_failed: "Monthly Digest Email Failed",
  monthly_digest_run_completed: "Monthly Digest — Run Completed",
  subscription_renewal_identity_mismatch_blocked: "Subscription Renewal Notice BLOCKED — Identity Mismatch (Admin Action Needed)",
  payment_identity_mismatch_status_unknown: "Payment Identity-Mismatch Status Unknown — Legacy Row (Admin Action Needed)",
  subscription_payment_failed_identity_mismatch_blocked: "Subscription Payment-Failed Notice BLOCKED — Identity Mismatch (Admin Action Needed)",
  subscription_cancel_identity_mismatch_blocked: "Subscription Cancellation Notice BLOCKED — Identity Mismatch (Admin Action Needed)",
  // 2026-10-16 follow-up (verifier-flagged real gap, Item 17 close-out) —
  // the /my-monthly-digest patient-facing read route (Part 11c) has its
  // own distinct "view" action family, separate from the cron-trigger
  // "run" family above — missed by the first diff pass since these call
  // sites sit later in main.py than the cron-path block that pass read.
  monthly_digest_viewed: "Monthly Digest — Patient Viewed",
  monthly_digest_view_failed: "Monthly Digest — Patient View Failed",
  monthly_digest_view_narrative_decrypt_failed: "Monthly Digest — Narrative Decrypt Failed (Patient View)",
  monthly_digest_view_news_item_decrypt_failed: "Monthly Digest — News Item Decrypt Failed (Patient View)",
  // 2026-10-16 fix (Part 11c close-out, CEO session) — added after Part
  // 11c's own advisor pass caught a real silent-swallow gap: the
  // per-month specimen re-fetch had no audit trail at all on failure.
  monthly_digest_view_specimen_unresolved: "Monthly Digest — Specimen Could Not Be Resolved (Patient View)",
};

function StatCard({ label, value, isDark }) {
  return (
    <Box sx={{
      flex: "1 1 160px", backgroundColor: isDark ? "#111827" : "#FFF",
      border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8", borderRadius: 2, p: 2.25,
    }}>
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: isDark ? "#94a3b8" : "#888", mb: 0.75 }}>{label}</Typography>
      <Typography sx={{ fontSize: "1.65rem", fontWeight: 700, color: isDark ? "#f1f5f9" : "#111", fontFamily: "monospace" }}>{value}</Typography>
    </Box>
  );
}

// 2026-10-05 fix — real gaps, reported directly: "Lab" was a free-text
// field instead of a dropdown of real lab ids, and "Details" dumped raw
// JSON.stringify(row.details) (literal curly braces/quotes) instead of
// readable text.
// 2026-10-05 fix — real bug, confirmed against every current
// record_audit_event(..., details={...}) call site in main.py: today they
// all write flat string/int values, which this already rendered fine. But
// nothing stopped a future call from passing a nested object/array/null,
// and `${v}` on any of those prints the literal "[object Object]"/
// "null"/"undefined" string, not readable content — this is almost
// certainly what reads as "garbage" when it happens. formatValue below
// recurses into nested objects/arrays (comma-joined, same "key: value"
// style) so this stays readable no matter how rich a future details={}
// payload gets, instead of relying on every call site staying flat forever.
// 2026-10-24 fix (carried-forward finding #7, flow audit) — formatDetails
// below title-cased KEYS (e.g. "reason" -> "Reason") but never touched
// VALUES, so a details.reason value like "invalid_permissions_payload"
// rendered raw, unreadable snake_case — the exact gap this project's
// flow-map has carried forward since it was first found. Not realistic
// to hand-map literally every distinct reason string this codebase could
// ever write (same practical reality as ACTION_LABELS above, which is
// large but not provably exhaustive either) — REASON_LABELS covers every
// reason string actually confirmed via direct code reads this session
// (fetch_files/download/delete, update_labs/update_permissions,
// admin_patient_test_results, verify-npi proxy, etc.), and
// humanizeFallback below makes anything NOT in the map at least readable
// (snake_case -> Title Case) rather than leaving it raw.
const REASON_LABELS = {
  not_found: "Not Found",
  user_not_found: "User Not Found",
  no_valid_fields: "No Valid Fields Provided",
  invalid_permissions_payload: "Invalid Permissions Payload",
  invalid_patient_id: "Invalid Patient ID",
  invalid_response_body: "Invalid Response Body",
  invalid_provided_json: "Invalid Provided JSON",
  invalid_provider_npi: "Invalid Provider NPI",
  no_identity_id: "No Identity On File",
  not_patient: "Not a Patient Account",
  no_hash_on_file: "No Identity Hash On File",
  wrong_password: "Wrong Password",
  missing_password: "Password Required",
  manual_provider_name_required: "Provider Name Required",
  provider_name_required: "Provider Name Required",
  practice_name_required: "Practice Name Required",
  portal_db_not_configured: "Portal Database Not Configured",
  gcs_bucket_not_configured: "GCS Bucket Not Configured",
  identity_service_not_configured: "Identity Service Not Configured",
  portal_internal_api_not_configured: "Portal Internal API Not Configured",
  clinician_field_decrypt_sentinel: "Clinician Field Decrypt Failed",
  identity_folder_decrypt_sentinel: "Identity Folder Decrypt Failed",
  linked_patient_record_missing_address: "Linked Patient Has No Address On File",
  timeout: "Request Timed Out",
};

function humanizeFallback(v) {
  // Only humanize genuine identifier-shaped tokens (lowercase, digits,
  // underscores) — never touch an exception class name (e.g.
  // "ConnectionError"), a free-text value, an email, or an id, which
  // would read worse title-cased than left alone.
  if (typeof v !== "string" || !/^[a-z0-9]+(_[a-z0-9]+)+$/.test(v)) return v;
  return v.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
}

function formatValue(v, key) {
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return v.map((vv) => formatValue(vv, key)).join(", ");
  if (typeof v === "object") {
    return Object.entries(v).map(([k, vv]) => `${k.replace(/_/g, " ")}: ${formatValue(vv, k)}`).join(", ");
  }
  if (key === "reason") return REASON_LABELS[v] || humanizeFallback(v);
  return String(v);
}

function formatDetails(details) {
  if (!details || Object.keys(details).length === 0) return "—";
  return Object.entries(details)
    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${formatValue(v, k)}`)
    .join(" · ");
}

export default function AuditDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // 2026-10-08 addition (audit click-through) — a row elsewhere (portal's
  // AdminDashboard.js, this app's own UserDashboard.js) can deep-link here
  // with ?source=portal&identity=someone@example.com to land pre-filtered
  // on that one person's history instead of the admin re-typing it by
  // hand. Read once via lazy useState initializers (not a separate effect)
  // so the very first render already has the right values — no empty-then-
  // filled flash, and nothing to race against the filter-reset below.
  const [searchParams, setSearchParams] = useSearchParams();
  // 2026-10-05 addition (plan Section C) — which audit log this page is
  // showing: this app's own staff actions (existing), or labariumaiportal's
  // patient/clinician actions (new). Separate endpoints, separate action
  // vocabularies, separate (smaller) column set for the portal source —
  // see PORTAL_ACTION_LABELS above.
  // 2026-10-26 addition (user-directed — "this is the main admin, this
  // should have everything") — a third option, "all", merges both
  // sources into one combined, time-sorted feed instead of forcing a
  // manual tab switch to see portal-side events. "staff"/"portal" stay
  // exactly as they were (own endpoint, own full server-side pagination,
  // own filter set) — "all" is additive, not a replacement.
  const [source, setSource] = useState(() => {
    const s = searchParams.get("source");
    return s === "portal" || s === "all" ? s : "staff";
  }); // "staff" | "portal" | "all"
  const [filters, setFilters] = useState(() => ({ username: searchParams.get("identity") || "", action: "", lab: "", dateFrom: "", dateTo: "" }));
  const [labs, setLabs] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  // 2026-10-15 fix (client-facing audit-log completeness sweep, HIGH
  // severity): a genuine fetch failure (500, network error, timeout — any
  // non-401 error) used to be silently swallowed by the catch block below,
  // leaving `rows` at its previous (often empty) value with no indication
  // anything went wrong. "rows.length === 0" then rendered the exact same
  // "No events match these filters." message as a real empty result —
  // the admin's own audit viewer, the one surface meant to catch problems
  // elsewhere, could itself silently hide a real outage.
  const [loadError, setLoadError] = useState(false);

  // 2026-10-26 — takes an explicit source ("staff"/"portal") rather than
  // always reading the outer `source` state, so the "all" merge below can
  // build correct params for BOTH real endpoints in one call — "all"
  // itself is never a valid value to send to either backend.
  const paramsFor = useCallback((forSource) => {
    const p = {};
    if (forSource === "staff") {
      if (filters.username) p.username = filters.username;
      if (filters.action) p.action = filters.action;
      if (filters.lab) p.lab = filters.lab;
    } else {
      if (filters.username) p.identity = filters.username;
      if (filters.action) p.action = filters.action;
    }
    if (filters.dateFrom) p.date_from = filters.dateFrom;
    if (filters.dateTo) p.date_to = filters.dateTo;
    return p;
  }, [filters]);
  const activeParams = useCallback(() => paramsFor(source === "all" ? "staff" : source), [paramsFor, source]);

  // 2026-10-10 fix — real race, confirmed directly: a filter change (and
  // the explicit "reset filters" click below) can change `filters` and
  // `page` across two separate renders (the page-reset effect fires only
  // after the filters-change render commits), so `load`'s identity can
  // change — and the mount effect re-fire — twice for one user action:
  // once with the OLD page against the NEW filters, then again with the
  // corrected page. If the first (stale) request's response lands after
  // the second (fresh) one, its setRows() call silently overwrites the
  // correct rows with a mismatched page/filter combination, which reads
  // as "random stuff" appearing — exactly what a full browser reload
  // "fixes", since that remounts with no in-flight stale requests at all.
  // requestIdRef guards against this: only the response from the most
  // recently-issued call is ever applied to state.
  const requestIdRef = useRef(0);
  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setLoadError(false);
    try {
      if (source === "staff") {
        const [logRes, summaryRes] = await Promise.all([
          axios.get(`${API}/api/admin/audit-log`, { params: { ...activeParams(), page, page_size: pageSize }, headers: authHeaders() }),
          axios.get(`${API}/api/admin/audit-log/summary`, { headers: authHeaders() }),
        ]);
        if (requestIdRef.current !== requestId) return; // a newer request already landed — don't clobber it with this stale one
        setRows(logRes.data.rows || []);
        setTotal(logRes.data.total || 0);
        setSummary(summaryRes.data);
      } else if (source === "portal") {
        const logRes = await axios.get(`${API}/api/admin/portal-audit-log`, { params: { ...activeParams(), page, page_size: pageSize }, headers: authHeaders() });
        if (requestIdRef.current !== requestId) return; // stale — see above
        setRows(logRes.data.rows || []);
        setTotal(logRes.data.total || 0);
        setSummary(null); // no portal-side summary endpoint — stat cards/breakdown only apply to the staff source
      } else {
        // 2026-10-26 addition — "all": merges both real endpoints into one
        // time-sorted feed. Each source keeps its OWN server-side paging
        // (not a single unified page number) — this fetches a recent
        // window from each (pageSize rows from each, same page number
        // applied to both) and merges client-side, which is a real,
        // correct recent-activity view but not a single seamless deep-
        // pagination across both backends (paging far back may show a
        // gap on one side before the other catches up — acceptable
        // trade-off for "what's happened recently, across both apps,
        // in one place" rather than building real cross-source
        // pagination into two independent, already-tested endpoints).
        // 2026-10-26 fix (ui-ux-checker finding, real UI sweep) — "All"
        // used to drop straight to the raw table with no Total Events/
        // Active Users cards, unlike the Staff Actions tab. Fetches
        // BOTH sources' own summary endpoints (get_audit_log_summary +
        // the new get_portal_audit_log_summary) alongside the row data
        // and sums them — safe to add directly since staff usernames and
        // portal identities are disjoint populations, never the same
        // person counted twice. No combined action_breakdown: the two
        // vocabularies don't overlap (same reasoning as hiding the
        // action-filter dropdown in this mode below), so a merged bar
        // chart would be more confusing than informative.
        const [staffRes, portalRes, staffSummaryRes, portalSummaryRes] = await Promise.all([
          axios.get(`${API}/api/admin/audit-log`, { params: { ...paramsFor("staff"), page, page_size: pageSize }, headers: authHeaders() }),
          axios.get(`${API}/api/admin/portal-audit-log`, { params: { ...paramsFor("portal"), page, page_size: pageSize }, headers: authHeaders() }),
          axios.get(`${API}/api/admin/audit-log/summary`, { headers: authHeaders() }),
          axios.get(`${API}/api/admin/portal-audit-log/summary`, { headers: authHeaders() }),
        ]);
        if (requestIdRef.current !== requestId) return;
        const staffRows = (staffRes.data.rows || []).map((r) => ({ ...r, _source: "staff" }));
        const portalRows = (portalRes.data.rows || []).map((r) => ({ ...r, _source: "portal" }));
        const merged = [...staffRows, ...portalRows].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
        setRows(merged);
        setTotal((staffRes.data.total || 0) + (portalRes.data.total || 0));
        setSummary({
          total_events: (staffSummaryRes.data.total_events || 0) + (portalSummaryRes.data.total_events || 0),
          unique_users: (staffSummaryRes.data.unique_users || 0) + (portalSummaryRes.data.unique_users || 0),
          action_breakdown: [],
        });
      }
    } catch (err) {
      if (requestIdRef.current !== requestId) return; // stale error — a newer request is still in flight or already resolved
      if (err?.response?.status === 401) { localStorage.removeItem("admin_token"); navigate("/admin-login"); }
      else setLoadError(true); // 2026-10-15 fix — see loadError's own comment above
    } finally {
      if (requestIdRef.current === requestId) setLoading(false); // don't let a stale request's finally hide the spinner for the fresh one
    }
  }, [activeParams, paramsFor, page, navigate, source]);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) { navigate("/admin-login"); return; }
    load();
    axios.get(`${API}/api/admin/labs`, { headers: authHeaders() })
      .then((res) => setLabs(res.data || []))
      .catch(() => {}); // non-fatal — the lab filter just stays empty if this fails
  }, [load, navigate]);

  useEffect(() => { setPage(1); }, [filters.username, filters.action, filters.lab, filters.dateFrom, filters.dateTo, source]);
  // 2026-10-08 fix (audit click-through) — this used to be a `[source]`
  // effect that unconditionally cleared filters on every source change,
  // including the very first render. That's exactly wrong for a deep link
  // that seeds source="portal" + filters.username from the URL: the effect
  // fired right after mount and wiped the pre-filled identity before `load`
  // ever ran with it. Filters still shouldn't carry across sources (real,
  // separate vocabularies), but now that only happens from an explicit tab
  // click — see the Button's onClick below — never from a mount/re-render.

  const actionLabels = source === "staff" ? ACTION_LABELS : PORTAL_ACTION_LABELS;

  // 2026-10-10 fix — real bug, reported directly ("when I click the
  // refresh button... some random stuff gets added — and when I do an
  // actual web browser refresh, they go away"): there was never an actual
  // refresh button on this page at all. The only icon in the filter row
  // was this RestartAlt ("reset filters") button — unlabeled, so it reads
  // visually as a refresh icon. Arriving here via the audit click-through
  // deep link (UserDashboard.js → ?source=portal&identity=someone@x.com)
  // seeds filters.username from the URL (line 230 above), narrowing the
  // view to one person's events. Clicking the "refresh-looking" icon wiped
  // that filter back to blank — suddenly showing every patient/clinician
  // event instead of just that one person's, which is exactly the "random
  // stuff gets added" the founder saw. The URL's ?identity= was never
  // cleared, though, so a real browser reload re-read it and restored the
  // narrow filtered view — "they go away." Fix: (1) give this button an
  // honest "Clear filters" label, (2) actually clear the URL params it
  // claims to reset so a clear doesn't silently get reverted by the next
  // reload, and (3) add a real, separately-labeled Refresh button below
  // that only re-fetches — same pattern as ReportsDashboard.js's own
  // handleRefresh — and never touches filters.
  const resetFilters = () => {
    setFilters({ username: "", action: "", lab: "", dateFrom: "", dateTo: "" });
    setSearchParams({});
  };
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const formatTime = (iso) => formatEST(iso); // 2026-10-04 — explicit instruction: every timestamp in this app shows in EST, labeled

  return (
    <AdminPageShell
      isDark={isDark}
      backPath="/admin"
      title="Audit Log"
      subtitle="Who did what, and when"
      actions={
        /* 2026-10-05 addition (plan Section C) — source toggle between
           this app's own staff actions and labariumaiportal's
           patient/clinician actions (consolidated here per the admin
           architecture decision, rather than reviving portal's own
           dormant admin page). */
        [["all", "All"], ["staff", "Staff Actions (this app)"], ["portal", "Patient & Clinician Actions (portal)"]].map(([key, label]) => (
          <Button key={key} size="small" variant={source === key ? "contained" : "outlined"}
            onClick={() => {
              if (key === source) return; // no-op click on the already-active tab — don't wipe filters for nothing
              setSource(key);
              setFilters({ username: "", action: "", lab: "", dateFrom: "", dateTo: "" }); // filters don't carry across sources — different vocabularies
            }}
            sx={{ textTransform: "none", fontSize: "0.78rem", ...(source === key ? { bgcolor: NAVY, "&:hover": { bgcolor: NAVY } } : {}) }}>
            {label}
          </Button>
        ))
      }
    >
      <Container maxWidth="md">
        {(source === "staff" || source === "all") && (
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <StatCard label="Total Events" value={summary ? summary.total_events.toLocaleString() : "…"} isDark={isDark} />
            <StatCard label="Active Users" value={summary ? summary.unique_users : "…"} isDark={isDark} />
          </Box>
        )}

        {summary?.action_breakdown?.length > 0 && (
          <Box sx={{
            backgroundColor: isDark ? "#111827" : "#FFF",
            border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8",
            borderRadius: 2, p: 2.5, mb: 3,
          }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: isDark ? "#e5e7eb" : "#333", mb: 1.5 }}>Breakdown by Action</Typography>
            {summary.action_breakdown.map((a) => {
              const pct = summary.total_events > 0 ? Math.round((a.count / summary.total_events) * 100) : 0;
              return (
                <Box key={a.action} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#94a3b8" : "#555", width: 200, flexShrink: 0 }}>{actionLabels[a.action] || a.action}</Typography>
                  <Box sx={{ flex: 1, height: 8, backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "#F0F0F0", borderRadius: 1, overflow: "hidden" }}>
                    <Box sx={{ width: `${pct}%`, height: "100%", backgroundColor: NAVY, borderRadius: 1 }} />
                  </Box>
                  <Typography sx={{ fontSize: "0.72rem", color: isDark ? "#64748b" : "#999", width: 40, textAlign: "right", fontFamily: "monospace" }}>{a.count}</Typography>
                </Box>
              );
            })}
          </Box>
        )}

        <Box sx={{
          backgroundColor: isDark ? "#111827" : "#FFF",
          border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8",
          borderRadius: 2, p: 2.5, mb: 2, display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end",
        }}>
          <TextField size="small" label={source === "all" ? "Username / Email" : source === "staff" ? "Username" : "Email"} value={filters.username} onChange={(e) => setFilters((f) => ({ ...f, username: e.target.value }))} sx={{ minWidth: 180 }} />
          {/* 2026-10-26 — the action dropdown is skipped entirely in "all"
              mode rather than merged: the two vocabularies don't overlap,
              so a single dropdown would either need two option groups
              (easy to misread as "applies to both") or silently only
              filter whichever source a picked action happens to belong
              to. Identity search + date range (below) already cover the
              common, cross-source case this tab exists for; action/lab
              filtering stays precise on the two single-source tabs. */}
          {source !== "all" && (
            <Select size="small" displayEmpty value={filters.action} onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))} sx={{ minWidth: 180 }}>
              <MenuItem value="">Any action</MenuItem>
              {Object.entries(actionLabels).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
            </Select>
          )}
          {source === "staff" && (
            <Select size="small" displayEmpty value={filters.lab} onChange={(e) => setFilters((f) => ({ ...f, lab: e.target.value }))} sx={{ minWidth: 170 }}>
              <MenuItem value="">Any lab</MenuItem>
              {labs.map((l) => <MenuItem key={l.lab_id} value={l.lab_id}>{l.display_name || l.lab_id}</MenuItem>)}
            </Select>
          )}
          {/* Final admin-audit-logging sweep (2026-10-17) — this page had
              no way to narrow results to a time window, only by identity/
              action/lab; an admin could find "everything for this patient"
              but never "this patient's activity in the last 2 weeks"
              without manually paging. Both backend endpoints already
              accept date_from/date_to (Eastern calendar day, same
              convention as the lab-orders date filter elsewhere in this
              app) — this just wires the UI to it. */}
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
            value={filters.dateFrom} onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
            sx={{ minWidth: 150 }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
            value={filters.dateTo} onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
            sx={{ minWidth: 150 }} />
          <Tooltip title="Clear filters">
            <IconButton size="small" aria-label="Clear filters" onClick={resetFilters}><RestartAlt fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton size="small" aria-label="Refresh" onClick={load}><Refresh fontSize="small" /></IconButton>
          </Tooltip>
        </Box>

        <Box sx={{
          backgroundColor: isDark ? "#111827" : "#FFF",
          border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8",
          borderRadius: 2, overflow: "hidden",
        }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#F7F7F8" }}>
                {["Time", source === "all" ? "User / Identity" : source === "staff" ? "User" : "Identity", "Action", source === "all" ? "Lab / Severity" : source === "staff" ? "Lab" : "Severity", "Details"].map((h) => (
                  <TableCell key={h} sx={{
                    fontSize: "0.63rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase",
                    color: isDark ? "#94a3b8" : "#555",
                    borderBottom: isDark ? "2px solid rgba(255,255,255,0.1)" : "2px solid #E4E4E7",
                  }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}><CircularProgress size={20} /></TableCell></TableRow>
              ) : loadError ? (
                <TableRow><TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                  <Typography sx={{ fontSize: "0.8rem", color: "#C0392B", mb: 1 }}>
                    Couldn't load the audit log. This is a real error, not an empty result — try again.
                  </Typography>
                  <Button size="small" variant="outlined" onClick={load} sx={{ textTransform: "none" }}>Retry</Button>
                </TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={5} sx={{ textAlign: "center", py: 4, color: "#AAA" }}>No events match these filters.</TableCell></TableRow>
              ) : rows.map((row) => (
                // 2026-10-05 fix (plan B.1) — array-index key on the one
                // table that's both filtered and paginated; now keyed on
                // the real Mongo _id the backend returns.
                <TableRow key={row._id} sx={{ "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "transparent" } }}>
                  <TableCell sx={{ fontSize: "0.75rem", color: isDark ? "#94a3b8" : "#666", whiteSpace: "nowrap", borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : undefined }}>{formatTime(row.created_at)}</TableCell>
                  {/* 2026-10-09 fix — real bug, confirmed directly ("emails not
                      getting decrypted"): several clinician-only actions log
                      under an opaque sub, not an email, and the raw `identity`
                      was shown verbatim for those rows even though the backend
                      (get_portal_audit_log) now resolves a human-readable email
                      for every row. resolved_identity falls back to the raw
                      identity itself when no matching portal user exists (e.g.
                      a login-failure event for an account that was never
                      created), so this is a strict improvement over showing
                      `identity` directly. */}
                  <TableCell sx={{ fontFamily: "monospace", fontSize: "0.78rem", color: isDark ? "#e5e7eb" : "inherit", borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : undefined }}>{(row._source || source) === "staff" ? row.username : (row.resolved_identity || row.identity)}</TableCell>
                  {/* 2026-10-26 — row-level map lookup (row._source), not
                      the page-level `actionLabels`: in "all" mode a single
                      merged list holds rows from both vocabularies at
                      once, so the page-level map alone would mislabel
                      (or fail to label) whichever source isn't currently
                      selected. Falls back to `source` for the staff/
                      portal-only tabs, where every row shares one source
                      anyway — unchanged behavior there. */}
                  <TableCell sx={{ fontSize: "0.78rem", color: isDark ? "#e5e7eb" : "inherit", borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : undefined }}>{((row._source || source) === "staff" ? ACTION_LABELS : PORTAL_ACTION_LABELS)[row.action] || row.action}</TableCell>
                  {/* 2026-10-21 fix (chunk-10 trace finding, confirmed real)
                      — this only highlighted severity === "warning" in red;
                      severity === "error" (grepped: used for the MORE
                      severe class across labariumaiportal — payment/
                      subscription identity-mismatch-blocked events,
                      payment_webhook_unrecorded, item_payment_amount_
                      mismatch_blocked, monthly_digest PHI-rejection events)
                      rendered in the same plain gray as routine "info" rows
                      — the dashboard was highlighting the common/routine
                      tier and burying the rarer, genuinely severe one.
                      "error" now gets the strongest highlight, "warning" a
                      visibly distinct but lesser one, "info" stays neutral. */}
                  <TableCell sx={{
                    fontSize: "0.75rem",
                    fontWeight: row.severity === "error" ? 700 : 400,
                    color: row.severity === "error" ? "#dc2626" : row.severity === "warning" ? "#e05c5c" : (isDark ? "#94a3b8" : "#555"),
                    borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : undefined,
                  }}>
                    {(row._source || source) === "staff" ? (row.lab || "—") : (row.severity || "—")}
                  </TableCell>
                  {/* 2026-10-11 fix — real bug, reported directly: the
                      previous hover-tooltip-only approach (truncated text,
                      full text only on hover) is invisible on touch devices
                      (no hover event at all) and easy to miss even on
                      desktop, reading as "details are broken/not proper".
                      Always show the full text, wrapped — no truncation, no
                      interaction required to read it. */}
                  <TableCell sx={{ fontSize: "0.72rem", color: isDark ? "#64748b" : "#999", maxWidth: 320, whiteSpace: "normal", wordBreak: "break-word", borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : undefined }}>
                    {formatDetails(row.details)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ display: "flex", justifyContent: "space-between", px: 2, py: 1.5, borderTop: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid #E4E4E7" }}>
            <Typography sx={{ fontSize: "0.72rem", color: isDark ? "#94a3b8" : "#888" }}>
              {total === 0 ? "0 results" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft fontSize="small" /></IconButton>
              <Typography sx={{ fontSize: "0.72rem", color: isDark ? "#e5e7eb" : "inherit" }}>Page {page} of {totalPages}</Typography>
              <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}><ChevronRight fontSize="small" /></IconButton>
            </Box>
          </Box>
        </Box>
      </Container>
    </AdminPageShell>
  );
}
